import { useContext, useEffect, useState } from 'react';

import { Breadcrumb, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import OrganizationHeader from '@components/OrganizationHeader/OrganizationHeader';
import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import { IClinic } from '@customTypes/clinician';
import useUsers from '@hooks/useUsers';

import AddUsers from './components/AddUsers/AddUsers';
import DelegateManagement from './components/DelegateManagement/DelegateManagement';
import UserRoleManagement from './components/UserRoleManagement/UserRoleManagement';
import ClinicDetails from './components/ClinicDetails/ClinicDetails';

import './UserManagement.scss';
import { Context } from '../../store/store';

export default function UserManagement() {
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const { fetchUsers, isLoading: isLoadingUsers } = useUsers();
  const [selectedOrganization, setSelectedOrganization] = useState<IClinic | null>(null);

  const adminClinics = state.adminClinics;
  const isLoading = isLoadingUsers || !selectedOrganization?.clinic_id;

  useEffect(() => {
    if (adminClinics.length > 0 && !selectedOrganization) {
      setSelectedOrganization(adminClinics[0]);
    }
  }, [adminClinics, selectedOrganization]);

  useEffect(() => {
    if (selectedOrganization?.clinic_id) {
      fetchUsers(selectedOrganization.clinic_id, true);
    }
  }, [fetchUsers, selectedOrganization]);

  const goBack = () => {
    navigate(-1);
  };

  if (!adminClinics.length) {
    navigate('/');
    return null;
  }

  return (
    <Stack gap={4} className='layout-single-col-py user-management-container'>
      <Breadcrumb listProps={{ className: 'mb-0' }}>
        <Breadcrumb.Item onClick={goBack}>Return</Breadcrumb.Item>
        <Breadcrumb.Item active>Settings</Breadcrumb.Item>
        <Breadcrumb.Item active>Manage Users</Breadcrumb.Item>
      </Breadcrumb>

      <Stack as='section' className='page-title-section'>
        <h1 className='mb-0 page-title'>Manage Users</h1>
        <div className='text-secondary page-subtitle'>
          <p className='mb-2'>
            Manage users for your organization by assigning delegates to an ordering provider&apos;s clinical team, or
            by assigning organization default delegates to order on behalf of all ordering providers<sup>†</sup> in your
            organization.
          </p>
          <p className='mb-0 fs-xs'>
            <sup>†</sup>An ordering provider is a physician of record with a verified NPI.
          </p>
        </div>
      </Stack>

      <section className='section-wrapper'>
        <OrganizationHeader
          clinics={adminClinics}
          selectedOrganization={selectedOrganization}
          onSelect={setSelectedOrganization}
        />

        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <Stack gap={4}>
            <AddUsers clinicId={selectedOrganization?.clinic_id} />
            <UserRoleManagement clinicId={selectedOrganization?.clinic_id} />
            <DelegateManagement clinicId={selectedOrganization?.clinic_id} />
            {selectedOrganization && (
              <ClinicDetails clinicId={selectedOrganization.clinic_id} clinicName={selectedOrganization.name} />
            )}
          </Stack>
        )}
      </section>
    </Stack>
  );
}
